// ============================================================================
// Directional hover — vanilla port of jquery-directional-hover
// ----------------------------------------------------------------------------
// 1:1 reproduction of the algorithm in:
//   https://github.com/im-sticky/jQuery-directional-hover
//   src/jquery.directional-hover.js
//
// Algorithm:
//   1. Determine which corner-quadrant (TL/TR/BL/BR) the cursor is in using
//      four bit flags (T=1, R=2, B=4, L=8) XOR'd based on cursor position
//      relative to the element's centre.
//   2. For that corner, run a diagonal-line test to decide which of the two
//      adjacent edges the cursor actually crossed.
//   3. Translate that into an off-screen offset:
//        top edge    → top: -h*2, left: 0
//        bottom edge → top:  h*2, left: 0
//        left edge   → top: 0,    left: -w*2
//        right edge  → top: 0,    left:  w*2
//      (Note: *2, not 100%. This matches the original plugin exactly.)
//   4. On enter ('in'):  snap to the offset, then animate to top:0 left:0.
//   5. On leave ('out'): snap to top:0 left:0, then animate to the offset.
//
// Container/overlay CSS is set inline by the plugin to match what the
// original applied via $.fn.css():
//   .dh-container { position: relative; overflow: hidden; }
//   .dh-overlay   { position: absolute; top: -100%; }
//
// Defaults match the plugin defaults:
//   { overlay: '.dh-overlay', easing: 'swing', speed: 400 }
// ============================================================================

const DEFAULTS = {
  overlay: ".dh-overlay",
  easing: "swing",
  speed: 400,
};

const FLAG_T = 1;
const FLAG_R = 2;
const FLAG_B = 4;
const FLAG_L = 8;
const tlMask = FLAG_T | FLAG_L;
const trMask = FLAG_T | FLAG_R;
const blMask = FLAG_B | FLAG_L;
const brMask = FLAG_B | FLAG_R;

// jQuery's "swing" is a cosine-based ease that closely matches the CSS
// cubic-bezier(.02,.01,.47,1). "linear" passes through unchanged.
const EASING_MAP = {
  swing: "cubic-bezier(.02,.01,.47,1)",
  linear: "linear",
};

function resolveEasing(easing) {
  return EASING_MAP[easing] || easing;
}

function testMask(flags, mask) {
  return (flags & mask) === mask;
}

function testTopLeftToBottomRight(x, y, w, h) {
  return h * x - w * y < 0;
}

function testBottomRightToTopLeft(x, y, w, h) {
  return w * (y - h) + h * x - w * h < 0;
}

// Returns [top, left] in pixels for the off-screen offset.
function findSide(flags, x, y, w, h) {
  if (testMask(flags, tlMask)) {
    // TL corner: above-diagonal → left edge, below-diagonal → top edge.
    return testTopLeftToBottomRight(x, y, w, h) ? [0, -w * 2] : [-h * 2, 0];
  }
  if (testMask(flags, trMask)) {
    // TR corner: above-antidiagonal → top, below → right.
    return testBottomRightToTopLeft(x, y, w, h) ? [-h * 2, 0] : [0, w * 2];
  }
  if (testMask(flags, blMask)) {
    // BL corner: above-antidiagonal → left, below → bottom.
    return testBottomRightToTopLeft(x, y, w, h) ? [0, -w * 2] : [h * 2, 0];
  }
  if (testMask(flags, brMask)) {
    // BR corner: above-diagonal → bottom, below → right.
    return testTopLeftToBottomRight(x, y, w, h) ? [h * 2, 0] : [0, w * 2];
  }
  return [0, 0];
}

function slideOverlay(
  overlay,
  direction,
  px,
  py,
  ew,
  eh,
  ex,
  ey,
  speed,
  easing,
) {
  // Build the corner-quadrant flags. The plugin uses separate ifs with XOR;
  // exactly one of T/B and one of L/R toggles on per call, yielding a corner.
  let flags = 0;
  if (py - ey <= eh / 2) flags ^= FLAG_T;
  if (px - ex >= ew / 2) flags ^= FLAG_R;
  if (py - ey > eh / 2) flags ^= FLAG_B;
  if (px - ex < ew / 2) flags ^= FLAG_L;

  const [top, left] = findSide(flags, px - ex, py - ey, ew / 2, eh / 2);
  setOverlayPosition(overlay, direction, top, left, speed, easing);
}

function setOverlayPosition(overlay, direction, top, left, speed, easing) {
  const t = `top ${speed}ms ${resolveEasing(easing)}, left ${speed}ms ${resolveEasing(easing)}`;

  if (direction === "in") {
    // Snap to the off-screen offset, no transition.
    overlay.style.transition = "none";
    overlay.style.top = `${top}px`;
    overlay.style.left = `${left}px`;
    // Force reflow so the no-transition snap commits before re-enabling.
    void overlay.offsetWidth;
    // Animate to (0, 0).
    overlay.style.transition = t;
    overlay.style.top = "0px";
    overlay.style.left = "0px";
  } else if (direction === "out") {
    // Snap to (0, 0), then animate out to the offset on the exit side.
    overlay.style.transition = "none";
    overlay.style.top = "0px";
    overlay.style.left = "0px";
    void overlay.offsetWidth;
    overlay.style.transition = t;
    overlay.style.top = `${top}px`;
    overlay.style.left = `${left}px`;
  }
}

// Public API — mirrors $(selector).directionalHover(options).
export function directionalHover(selector, options) {
  const opts = Object.assign({}, DEFAULTS, options || {});
  const containers =
    typeof selector === "string"
      ? document.querySelectorAll(selector)
      : selector;

  containers.forEach(function (container) {
    // Same inline styles the plugin applies via $.fn.css().
    container.style.position = "relative";
    container.style.overflow = "hidden";

    const overlay = container.querySelector(opts.overlay);
    if (!overlay) return;

    overlay.style.position = "absolute";
    overlay.style.top = "-100%";

    container.addEventListener("mouseenter", function (e) {
      const rect = container.getBoundingClientRect();
      slideOverlay(
        overlay,
        "in",
        e.pageX,
        e.pageY,
        container.offsetWidth,
        container.offsetHeight,
        Math.floor(rect.left + window.pageXOffset),
        rect.top + window.pageYOffset,
        opts.speed,
        opts.easing,
      );
    });

    container.addEventListener("mouseleave", function (e) {
      const rect = container.getBoundingClientRect();
      slideOverlay(
        overlay,
        "out",
        e.pageX,
        e.pageY,
        container.offsetWidth,
        container.offsetHeight,
        Math.floor(rect.left + window.pageXOffset),
        rect.top + window.pageYOffset,
        opts.speed,
        opts.easing,
      );
    });
  });
}

// Auto-init with the same call the original Oxygen page used.
document.addEventListener("DOMContentLoaded", function () {
  directionalHover(".dh-container", {
    overlay: ".dh-overlay",
    easing: "swing",
    speed: 300,
  });
});
